/**
 * Execution block: KHR_interactivity standard `pointer/interpolate` op —
 * animates a transform component (translation, rotation, scale) on a
 * SceneObject toward a target value, using our internal TransformPath
 * addressing (like PointerSetBlock).
 *
 * Value inputs:
 *   - `value`    — Vec3 for translation/scale, quaternion [x,y,z,w] for rotation
 *   - `duration` — seconds; <= 0 or missing → snap write, `done` fires
 *                  immediately after `out`
 *   - `p1`, `p2` — cubic-bezier control points [x1,y1] / [x2,y2] (CSS
 *                  cubic-bezier semantics — every Trace easing IS one of
 *                  these); missing/invalid → easeInOut's points
 *
 * Flow outputs (KHR semantics):
 *   - `out`  — immediately after the interpolation starts
 *   - `done` — when the interpolation completes. A replacing interpolation or
 *              a snap write (pointer/set) on the SAME component cancels the
 *              pending `done` — the replacement's own `done` fires instead.
 *   - `err`  — instead of `out` when the target can't resolve, the property
 *              is unknown, or the value doesn't parse for the property.
 *
 * Rides the SceneObject per-component transform-transition channels, so
 * concurrent interpolations on different components of one target run
 * independently.
 */

import { FlowGraphAsyncExecutionBlock } from "@babylonjs/core/FlowGraph/flowGraphAsyncExecutionBlock";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeAny, RichTypeNumber } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { resolveTarget } from './traceContext';
import { toVec3, toQuat } from './PointerSetBlock';
import type { SceneObject } from '../../../core/SceneObject';
import type { TimeTransition } from '../../../scenedata/behaviors/propertiesdata';
import { EASING_CONTROL_POINTS } from '../../../core/canopy/TransitionUtils';
import { Logger } from '../../../utils/Logger';

export class PointerInterpolateBlock extends FlowGraphAsyncExecutionBlock {
    private _targetPath: string;
    private _property: string;
    /**
     * Bumped on every activation and on cancel — a pending `done` only fires
     * while its generation is current (same pattern as TracePlayExternalBlock's
     * `started`). Replacement through the SceneObject channel already drops the
     * superseded onComplete; the generation additionally guards graph disposal.
     */
    private _generation = 0;
    readonly err: FlowGraphSignalConnection;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string; property?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this._property = config?.property ?? 'translation';
        this.registerDataInput("value", RichTypeAny);
        this.registerDataInput("duration", RichTypeNumber, 0);
        this.registerDataInput("p1", RichTypeAny);
        this.registerDataInput("p2", RichTypeAny);
        this.err = this._registerSignalOutput("err");
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const target = resolveTarget(context, this._targetPath);
        if (!target) {
            this.err._activateSignal(context);
            return;
        }

        const rawValue: unknown = this.getDataInput("value")!.getValue(context);
        const rawDuration: unknown = this.getDataInput("duration")!.getValue(context);
        const duration = typeof rawDuration === 'number' && Number.isFinite(rawDuration) ? rawDuration : 0;

        // Resolve the component write up front so err can fire INSTEAD of out
        // (KHR: an invalid pointer or value activates only err).
        const write = this._resolveWrite(target, rawValue);
        if (!write) {
            this.err._activateSignal(context);
            return;
        }

        const gen = ++this._generation;

        if (duration <= 0) {
            // Snap write: out then done, immediately (KHR: a zero-length
            // interpolation completes right away).
            write(null, undefined);
            this.out._activateSignal(context);
            if (this._generation === gen) {
                this.done._activateSignal(context);
            }
            return;
        }

        const [x1, y1] = this._controlPoint("p1", context, 0);
        const [x2, y2] = this._controlPoint("p2", context, 2);
        const transition: TimeTransition = {
            duration,
            easing: `cubic-bezier(${x1}, ${y1}, ${x2}, ${y2})`,
        };

        // Starting the channel replaces any interpolation already running on
        // this component (same pointer) — the replaced one's done never fires.
        write(transition, () => {
            if (this._generation === gen) {
                this.done._activateSignal(context);
            }
        });
        this.out._activateSignal(context);
    }

    /**
     * Bind the property write for this activation: parses `rawValue` for the
     * configured property and returns a closure over the SceneObject channel
     * starter, or null when the property/value is invalid (→ err).
     */
    private _resolveWrite(
        target: SceneObject,
        rawValue: unknown,
    ): ((transition: TimeTransition | null, onComplete?: () => void) => void) | null {
        switch (this._property) {
            case 'translation': {
                const v = toVec3(rawValue);
                if (!v) {
                    Logger.error(`[PointerInterpolateBlock] Invalid translation value on "${target.name}"`);
                    return null;
                }
                return (transition, onComplete) => target._startPositionChannel(v, transition, onComplete);
            }
            case 'rotation': {
                const q = toQuat(rawValue);
                if (!q) {
                    Logger.error(`[PointerInterpolateBlock] Invalid rotation value on "${target.name}"`);
                    return null;
                }
                return (transition, onComplete) => target._startRotationChannel(q, transition, onComplete);
            }
            case 'scale': {
                const v = toVec3(rawValue);
                if (!v) {
                    Logger.error(`[PointerInterpolateBlock] Invalid scale value on "${target.name}"`);
                    return null;
                }
                return (transition, onComplete) => target._startScaleChannel(v, transition, onComplete);
            }
            default:
                Logger.error(`[PointerInterpolateBlock] Unknown property "${this._property}" on "${target.name}"`);
                return null;
        }
    }

    /**
     * Read a control-point input ([x, y] pair). Missing or unparsable →
     * easeInOut's corresponding pair from the alias table (`offset` selects
     * [x1,y1] vs [x2,y2]).
     */
    private _controlPoint(socket: 'p1' | 'p2', context: FlowGraphContext, offset: 0 | 2): [number, number] {
        const raw: unknown = this.getDataInput(socket)!.getValue(context);
        const pair = toVec2(raw);
        if (pair) return pair;
        const d = EASING_CONTROL_POINTS.easeInOut;
        return [d[offset], d[offset + 1]];
    }

    public override _preparePendingTasks(_context: FlowGraphContext): void { /* no-op */ }

    public override _cancelPendingTasks(_context: FlowGraphContext): void {
        this._generation++;
    }

    public override getClassName(): string {
        return "PointerInterpolateBlock";
    }
}

/** Convert an input value to an [x, y] pair (accepts Vector2, [x,y] arrays, or {x,y} objects). */
function toVec2(value: unknown): [number, number] | null {
    if (value instanceof Vector2) return [value.x, value.y];
    if (Array.isArray(value) && value.length >= 2
        && typeof value[0] === 'number' && typeof value[1] === 'number'
        && Number.isFinite(value[0]) && Number.isFinite(value[1])) {
        return [value[0], value[1]];
    }
    if (value != null && typeof value === 'object' && 'x' in value && 'y' in value) {
        const v = value as { x: number; y: number };
        if (Number.isFinite(v.x) && Number.isFinite(v.y)) return [v.x, v.y];
    }
    return null;
}
